import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from '../../app/store'
import {
  createDepartment,
  deleteDepartment,
  fetchDepartment,
  fetchDepartments,
  updateDepartment,
} from './api'
import {
  CreateDepartmentParams,
  Department,
  UpdateDepartmentParams,
} from './types'
import {
  DEPARTMENT_PERMISSIONS,
  DepartmentPermissions,
  DepartmentPermissionsIds,
} from './permissions'

export class DepartmentsStore {
  rootStore: RootStore
  departmentsById: Record<string, Department> = {}
  departmentIds: string[] = [] // To maintain order
  availablePermissions: DepartmentPermissions[] = []
  isLoadingDepartments = false
  initiallyLoaded = false // Flag to track initial load

  // Track loading state per department
  updatingDepartmentsById: Record<string, boolean> = {}

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  // Computed property to get departments as an array in the correct order
  get departments(): Department[] {
    return this.departmentIds
      .map((id) => this.departmentsById[id])
      .filter(Boolean)
  }

  setDepartmentUpdating(departmentId: string, updating: boolean) {
    this.updatingDepartmentsById[departmentId] = updating
  }

  isDepartmentUpdating(departmentId: string): boolean {
    return !!this.updatingDepartmentsById[departmentId]
  }

  async fetchDepartments() {
    // Skip if already loading
    if (this.isLoadingDepartments) return

    this.isLoadingDepartments = true

    try {
      const response = await fetchDepartments()
      const departments = response.docs || []

      runInAction(() => {
        // Clear previous data and update with new data
        this.departmentsById = {}
        this.departmentIds = []

        // Store departments by ID and maintain order
        departments.forEach((dept) => {
          this.departmentsById[dept.id] = dept
          this.departmentIds.push(dept.id)
        })

        this.isLoadingDepartments = false
        this.initiallyLoaded = true // Mark as initially loaded
        this.availablePermissions = DEPARTMENT_PERMISSIONS
      })

      return departments
    } catch (err) {
      runInAction(() => {
        this.isLoadingDepartments = false
      })
      console.error(err)
      throw err
    }
  }

  async fetchDepartmentById(departmentId: string) {
    this.setDepartmentUpdating(departmentId, true)

    try {
      const department = await fetchDepartment(departmentId)

      runInAction(() => {
        // Update or add the department in our data structure
        this.departmentsById[department.id] = department

        // Add to departmentIds array if it's not already there
        if (!this.departmentIds.includes(department.id)) {
          this.departmentIds.push(department.id)
        }

        this.setDepartmentUpdating(departmentId, false)
      })

      return department
    } catch (err) {
      runInAction(() => {
        this.setDepartmentUpdating(departmentId, false)
      })
      console.error(err)
      throw err
    }
  }

  async createDepartment(params: CreateDepartmentParams) {
    this.isLoadingDepartments = true

    try {
      const response = await createDepartment(params)

      // Fetch the newly created department directly
      await this.fetchDepartments()

      runInAction(() => {
        this.isLoadingDepartments = false
      })

      return response
    } catch (err) {
      runInAction(() => {
        this.isLoadingDepartments = false
      })
      console.error(err)
      throw err
    }
  }

  async updateDepartment(departmentId: string, params: UpdateDepartmentParams) {
    this.setDepartmentUpdating(departmentId, true)

    try {
      const response = await updateDepartment(departmentId, params)

      // Fetch only the updated department to get the complete data
      await this.fetchDepartmentById(departmentId)

      // Re-fetch self-permissions when department is updated
      await this.rootStore.auth.fetchUserPermissions()

      return response
    } catch (err) {
      runInAction(() => {
        this.setDepartmentUpdating(departmentId, false)
      })
      console.error(err)
      throw err
    }
  }

  async deleteDepartment(departmentId: string) {
    this.setDepartmentUpdating(departmentId, true)

    try {
      await deleteDepartment(departmentId)

      // Remove from local state
      runInAction(() => {
        // Remove from our map
        delete this.departmentsById[departmentId]

        // Remove from the order array
        this.departmentIds = this.departmentIds.filter(
          (id) => id !== departmentId,
        )

        this.setDepartmentUpdating(departmentId, false)
      })

      // Re-fetch self-permissions when department is updated
      await this.rootStore.auth.fetchUserPermissions()
    } catch (err) {
      runInAction(() => {
        this.setDepartmentUpdating(departmentId, false)
      })
      console.error(err)
      throw err
    }
  }

  async updateDepartmentPermissions(
    departmentId: string,
    permissions: DepartmentPermissionsIds[],
  ) {
    const response = await this.updateDepartment(departmentId, {
      permissions,
    })

    return response
  }

  async addMemberToDepartment(departmentId: string, userId: string) {
    this.setDepartmentUpdating(departmentId, true)

    try {
      const department = this.departmentsById[departmentId]
      if (!department) {
        throw new Error('Department not found')
      }

      const currentMembers = department.members || []
      const memberIds = currentMembers.map((m) => m.id)

      // Check if user is already a member
      if (memberIds.includes(userId)) {
        this.setDepartmentUpdating(departmentId, false)
        return department
      }

      // Add the member
      const updatedMemberIds = [...memberIds, userId]
      const response = await this.updateDepartment(departmentId, {
        members: updatedMemberIds,
      })

      return response
    } catch (err) {
      runInAction(() => {
        this.setDepartmentUpdating(departmentId, false)
      })
      console.error(err)
      throw err
    }
  }

  async removeMemberFromDepartment(departmentId: string, userId: string) {
    this.setDepartmentUpdating(departmentId, true)

    try {
      const department = this.departmentsById[departmentId]
      if (!department) {
        throw new Error('Department not found')
      }

      const members = department.members || []
      const updatedMemberIds = members
        .filter((m) => m.id !== userId)
        .map((m) => m.id)

      const response = await this.updateDepartment(departmentId, {
        members: updatedMemberIds,
      })

      return response
    } catch (err) {
      runInAction(() => {
        this.setDepartmentUpdating(departmentId, false)
      })
      console.error(err)
      throw err
    }
  }

  // Space-related methods have been removed as the relationship is now managed by the Spaces collection
}
